import gsap from 'gsap'
import ArrayBuffer from 'nanogl/arraybuffer'
import IndexBuffer from 'nanogl/indexbuffer'
import Program from 'nanogl/program'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import GLState, { LocalConfig } from 'nanogl-state/GLState'

import { RenderContext } from '@/webgl/core/RenderContext'
import RenderMask from '@/webgl/core/RenderMask'
import Programs from '@/webgl/glsl/programs'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'

export default class SpotlightsRenderer implements ILoadable {
  active: boolean = false
  node: Node

  prg: Program
  cfg: LocalConfig
  vbuffer: ArrayBuffer
  ibuffer: IndexBuffer
  time: number = 0
  alpha: number = 0

  setActive(flag: boolean): void {
    this.active = flag
    if (this.active) {
      this.world.root.add(this.node)
    } else {
      this.world.root.remove(this.node)
    }
  }

  constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.node = new Node()
    this.node.z = 0
    this.prg = Programs(gl).get('spotlights')
    this.cfg = GLState.get(gl).config()
    this.cfg
      .enableDepthTest(true)
      .depthMask(false)
      .enableCullface(true)
      .cullFace(gl.BACK)
      .enableBlend(true)
      .blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
  }

  load(abort: AbortSignal): Promise<unknown> {
    if (!this.active) return Promise.resolve()
    this.generate()
    return Promise.resolve()
  }

  reveal() {
    gsap.to(this, { alpha: 1, duration: 0.5 })
  }

  generate() {
    const { settings } = this.world

    const gl = this.gl
    this.vbuffer = new ArrayBuffer(gl)
    this.vbuffer.attrib('aPosition', 3, gl.FLOAT).attrib('aTexCoord', 2, gl.FLOAT).attrib('aSeeds', 4, gl.FLOAT)

    this.ibuffer = new IndexBuffer(gl, gl.UNSIGNED_SHORT)

    const size = 0.18
    const sz = 2.15
    const vertex = []
    const index = []
    let offset = 0
    for (let i = 0; i < settings.LANE_COUNT; i++) {
      const seed_1 = Math.random()
      const seed_2 = Math.random()
      const seed_3 = Math.random()
      const seed_4 = Math.random()
      const seed = [seed_1, seed_2, seed_3, seed_4]

      const lane = i - 1

      vertex.push(-size, -size * sz, lane)
      vertex.push(0, 0)
      vertex.push(...seed)

      vertex.push(size, -size * sz, lane)
      vertex.push(1, 0)
      vertex.push(...seed)

      vertex.push(size, size * sz, lane)
      vertex.push(1, 1)
      vertex.push(...seed)

      vertex.push(-size, size * sz, lane)
      vertex.push(0, 1)
      vertex.push(...seed)

      index.push(offset + 0, offset + 2, offset + 1)
      index.push(offset + 0, offset + 3, offset + 2)
      offset += 4
    }

    this.vbuffer.data(new Float32Array(vertex))
    this.ibuffer.data(new Uint16Array(index))
  }

  public preRender(time: TimeEntity) {
    if (!this.active) return

    this.time += time.unscaledDeltaTime * this.world.settings.scrollSpeed * this.alpha
  }

  render(context: RenderContext) {
    if (!this.active) return

    if ((context.mask & RenderMask.OPAQUE) === 0) return

    this.prg.use()
    this.prg.uMVP(context.camera.getMVP(this.node._wmatrix))
    this.prg.uTime(this.time)
    this.prg.uReveal(this.alpha)

    this.vbuffer.bind()
    this.vbuffer.attribPointer(this.prg)

    this.cfg.apply()

    this.ibuffer.bind()
    this.ibuffer.drawTriangles()
  }
}
